'use client';

import { useState, useEffect, useContext } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import { RadioGroup } from '@headlessui/react';
import { CheckCircleIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import TeamSettings from './TeamSettings';
import PendingInvite from './PendingInvites';


export default function TeamPage() {
  const { user } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true); // Loading state for the organization fetch

  useEffect(() => {
    if (!user) return;

    const fetchOrganization = async () => {
      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select('organizations(id, name)')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching organization:', error);
        } else {
          setOrganization(data?.organizations || null);
        }
      } catch (err) {
        console.error('Error fetching organization:', err);
      } finally {
        setLoading(false); // Stop loading once the fetch is complete
      }
    };

    fetchOrganization();
  }, [user]);

  if (loading) {
    // Skeleton loader while fetching organization
    return (
      <div>
      </div>
    );
  }

  return (
    
    <div>
      <div className="mt-6">
      <PendingInvite userId={user.id} />
        {organization ? (
          <TeamSettings organizationId={organization.id} />
        ) : (
          <JoinOrCreateCompany user={user} />
        )}
      </div>
    </div>
  );
}

function JoinOrCreateCompany({ user }) {
  const [selectedOption, setSelectedOption] = useState('join'); // 'join' or 'create'

  const options = [
    { id: 'join', title: 'Join an Existing Company', description: 'Get the Company Key from the Owner and enter to join.' },
    { id: 'create', title: 'Create a New Company', description: 'Start your own company.' },
  ];

  return (
    <div>
      {/* <div className="border-l-4 border-yellow-400 bg-yellow-50 p-4 my-4">
        <div className="flex">
          <div className="shrink-0">
            <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              You are not a member of a Company. Please join an existing company or create a new one.
            </p>
          </div>
        </div>
      </div> */}
      <RadioGroup
        value={selectedOption}
        onChange={setSelectedOption}
        className="mt-6 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
      >
        {options.map((option) => (
          <RadioGroup.Option
            key={option.id}
            value={option.id}
            className={({ checked }) =>
              `group relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none ${
                checked ? 'border-yellow-600 ring-1 ring-yellow-600' : 'border-gray-300'
              }`
            }
          >
            {({ checked }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <span className="block text-sm font-medium text-gray-900">{option.title}</span>
                    <span className="mt-1 text-sm text-gray-500">{option.description}</span>
                  </span>
                </span>
                <CheckCircleIcon
                  className={`size-5 ${
                    checked ? 'text-yellow-600' : 'invisible'
                  }`}
                  aria-hidden="true"
                />
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute -inset-px rounded-lg ${
                    checked ? 'border-2 border-yellow-600' : 'border-2 border-transparent'
                  }`}
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>

      <div className="mt-6">
        {selectedOption === 'join' && <JoinCompany user={user} />}
        {selectedOption === 'create' && <CreateCompany user={user} />}
      </div>
    </div>
  );
}


function JoinCompany({ user }) {
  const [searchKey, setSearchKey] = useState('');
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSearchKeyChange = async (key) => {
    setSearchKey(key);
    setSelectedOrganization(null);
    setErrorMessage('');
    setSuccessMessage('');

    if (key.trim() === '') {
      setOrganizationOptions([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name')
        .eq('organization_key', key);

      if (error) {
        throw error;
      }

      setOrganizationOptions(data || []);
    } catch (err) {
      console.error('Error searching for organization:', err.message);
      setErrorMessage('Failed to search for organizations.');
    }
  };

  const handleJoinCompany = async () => {
    if (!selectedOrganization) {
      setErrorMessage('Please select an organization to join.');
      return;
    }
  
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');
  
    try {
      // Step 1: Fetch team usage data
      const response = await fetch('https://nodejs-serverless-function-express-azure-delta.vercel.app/api/team-usage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ organizationId: selectedOrganization.id }),
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch team usage data.');
      }
  
      const teamUsage = await response.json();
  
      // Step 2: Check if spots are available
      if (teamUsage.remainingSpots <= 0) {
        setErrorMessage(`No available spots in ${selectedOrganization.name}. Please contact the admin.`);
        setLoading(false);
        return;
      }
  
      // Step 3: Proceed with joining the company
      const { error } = await supabase
        .from('organization_members')
        .insert([{ user_id: user.id, organization_id: selectedOrganization.id, role: 'member' }]);
  
      if (error) throw error;
  
      setSuccessMessage(`Successfully joined the organization: ${selectedOrganization.name}`);
  
      // Reload page to reflect changes
      window.location.reload();
      
      // Reset state
      setSearchKey('');
      setOrganizationOptions([]);
      setSelectedOrganization(null);
    } catch (err) {
      setErrorMessage(err.message || 'Failed to join the organization.');
    } finally {
      setLoading(false);
    }
  };
  

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-600">Join a Company</h3>
      <div className="relative mt-2">
        <input
          type="text"
          {...(selectedOrganization && { disabled: true, placeholder: '' })}
          placeholder={selectedOrganization ? '' : 'Enter Company Key'}
          value={searchKey}
          onChange={(e) => handleSearchKeyChange(e.target.value)}
          className="block w-full max-w-md rounded-md border border-gray-300 px-4 py-3 text-sm focus:border-yellow-500 focus:ring-yellow-500"
        />

        {/* Render the dropdown options if no organization is selected */}
        {!selectedOrganization && organizationOptions.length > 0 && (
          <div className="absolute z-10 mt-2 w-full max-w-md bg-white border border-gray-300 rounded-md shadow-md">
            {organizationOptions.map((org) => (
              <div
                key={org.id}
                onClick={() => { handleSearchKeyChange(''); setSelectedOrganization(org); }} // Set the selected organization
                className={`cursor-pointer px-4 py-2 text-sm ${
                  selectedOrganization?.id === org.id
                    ? 'bg-yellow-100 text-yellow-600'
                    : 'hover:bg-gray-100'
                }`}
              >
                {org.name}
              </div>
            ))}
          </div>
        )}
        {/* Chip inside input field */}
        {selectedOrganization && (
          <div className="absolute left-2 top-1/2 -translate-y-1/2 flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm border border-gray-300">
            <span className="text-gray-700 font-medium truncate">{selectedOrganization.name}</span>
          </div>
        )}
      </div>

      {selectedOrganization && (
        <div className="mt-4">
          <p className="text-sm font-semibold text-gray-700">
            Selected Organization: {selectedOrganization.name}
          </p>
        </div>
      )}

      <div className="flex gap-4">
        <button
          onClick={handleJoinCompany}
          className="mt-4 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-500"
          disabled={loading || !selectedOrganization}
        >
          {loading ? 'Joining...' : 'Join Company'}
        </button>
        {selectedOrganization && (
          <button
            onClick={() => setSelectedOrganization(null)}
            className="mt-4 rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100 border border-gray-300"
          >
            Cancel
          </button>
        )}
      </div>
      {successMessage && <p className="mt-4 text-sm text-green-600">{successMessage}</p>}
      {errorMessage && <p className="mt-4 text-sm text-red-600">{errorMessage}</p>}
    </div>
  );
}

function CreateCompany({ user }) {
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [profile, setProfile] = useState({ first_name: '', last_name: '' }); // Profile state
  const { session } = useContext(AuthContext); // Get session from AuthContext


  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        const { data, error } = await supabase
          .from('profile')
          .select('first_name, last_name')
          .eq('id', user.id)
          .single();

        if (!error && data) {
          setProfile({ first_name: data.first_name, last_name: data.last_name });
        } else {
          console.error('Error fetching profile:', error);
        }
      }
    };

    fetchProfile();
  }, [user]);

  const handleCreateCompany = async () => {
    setLoading(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      // Call the new integrated edge function
      const response = await fetch('https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/create-company', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // Include authorization if needed
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          company_name: companyName
          // Note: we don't need to pass profile info as the edge function will fetch it
        }),
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || result.details || 'Failed to create company');
      }

      setSuccessMessage('Company created successfully');
      setCompanyName('');
      // Reload page to reflect changes
      window.location.reload();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to create company.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-600">Create a New Company</h3>
      <p className="text-sm text-gray-500 mt-2">You are creating a new Company. Please ensure that you are looking to do this, and not join an existing company.</p>
      <input
        type="text"
        placeholder="Company Name"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        className="block w-full max-w-md rounded-md border border-gray-300 px-4 py-3 text-sm focus:border-yellow-500 focus:ring-yellow-500 mt-2"
      />
      <button
        onClick={handleCreateCompany}
        className="mt-4 rounded-md bg-yellow-600 px-4 py-2 text-sm font-semibold text-white hover:bg-yellow-500"
        disabled={loading}
      >
        {loading ? 'Creating...' : 'Create Company'}
      </button>
      {successMessage && <p className="mt-4 text-sm text-green-600">{successMessage}</p>}
      {errorMessage && <p className="mt-4 text-sm text-red-600">{errorMessage}</p>}
    </div>
  );
}
